import React, { useCallback, useEffect, useState } from "react";
import { platformApi } from "../api";

const errMsg = (e) => e?.response?.data?.message || e?.message || "Request failed";
const mb = (n) => `${(Number(n) / 1048576).toFixed(1)} MB`;
const TONE = { Published: "#047857", Draft: "#64748b", Withdrawn: "#b91c1c" };

function Row({ r, reload, setErr }) {
  const [pct, setPct] = useState(r.rollout_percent);
  useEffect(() => setPct(r.rollout_percent), [r.rollout_percent]);
  const patch = async (body) => { try { await platformApi.patch(`/platform/releases/${r.id}`, body); setErr(""); reload(); } catch (e) { setErr(errMsg(e)); } };
  const publish = () => {
    if (r.status === "Published") return;
    if (window.confirm(`Publish ${r.component} ${r.version} (${r.os}/${r.arch})? It will reach ${pct}% of Agent Servers${pct === 0 ? " (none until you raise the rollout)" : ""}.`)) patch({ status: "Published" });
  };
  return (
    <tr>
      <td>{r.component}</td><td>{r.os}/{r.arch}</td><td><b>{r.version}</b></td><td>{r.channel}</td>
      <td><span className="pill" style={{ color: TONE[r.status], fontWeight: 700 }}>{r.status}</span></td>
      <td>
        <input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} style={{ width: 64 }} /> %
        <button className="btn sm" style={{ marginLeft: 6 }} disabled={Number(pct) === r.rollout_percent} onClick={() => patch({ rollout_percent: Number(pct) })}>Set</button>
        {[5, 25, 50, 100].map((p) => <button key={p} className="btn link" onClick={() => patch({ rollout_percent: p })}>{p}</button>)}
      </td>
      <td>{mb(r.size)}</td><td title={r.sha256} style={{ fontFamily: "monospace" }}>{r.sha256.slice(0, 10)}…</td>
      <td>
        {r.status !== "Published" && <button className="btn sm primary" onClick={publish}>Publish</button>}
        {r.status === "Published" && <button className="btn sm danger" onClick={() => window.confirm("Withdraw this release? Agent Servers stop being offered it; machines already updated keep it.") && patch({ status: "Withdrawn" })}>Withdraw</button>}
        {r.status === "Withdrawn" && <button className="btn sm" onClick={() => patch({ status: "Draft" })}>Back to draft</button>}
      </td>
    </tr>
  );
}

export default function Releases() {
  const [rels, setRels] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pins, setPins] = useState([]);
  const [err, setErr] = useState("");
  const [pin, setPin] = useState({ tenantId: "", component: "agent", mode: "hold", version: "", channel: "beta" });
  const load = useCallback(() => {
    platformApi.get("/platform/releases").then((r) => setRels(r.data?.data || [])).catch((e) => setErr(errMsg(e)));
    platformApi.get("/platform/tenants").then((r) => setTenants(r.data?.data || [])).catch(() => {});
    platformApi.get("/platform/release-pins").then((r) => setPins(r.data?.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const savePin = async (e) => {
    e.preventDefault();
    try {
      await platformApi.put(`/platform/tenants/${pin.tenantId}/release-pin`, { component: pin.component, mode: pin.mode, version: pin.version, channel: pin.channel });
      setErr(""); load();
    } catch (x) { setErr(errMsg(x)); }
  };
  const removePin = async (p) => { await platformApi.delete(`/platform/tenants/${p.tenant_id}/release-pin`, { params: { component: p.component } }); load(); };
  const tname = (id) => tenants.find((t) => t.id === id)?.slug || id;
  return (
    <div>
      <h1>Agents &amp; Releases</h1>
      <p className="sub">Signed builds of the agent, Agent Server and database scanner. New uploads start as Draft at 0%. Publish, then widen the rollout step by step; withdraw stops further offers.</p>
      {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar"><strong>Releases</strong><span className="sub" style={{ marginLeft: 8 }}>Upload with <code>releasectl publish</code> (signing key stays offline)</span></div>
        <table>
          <thead><tr><th>Component</th><th>Platform</th><th>Version</th><th>Channel</th><th>Status</th><th>Rollout</th><th>Size</th><th>SHA-256</th><th /></tr></thead>
          <tbody>
            {rels.map((r) => <Row key={r.id} r={r} reload={load} setErr={setErr} />)}
            {!rels.length && <tr><td colSpan={9} style={{ textAlign: "center", padding: 24 }}>No releases uploaded yet</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card">
        <div className="toolbar"><strong>Per-tenant overrides</strong><span className="sub" style={{ marginLeft: 8 }}>hold = no updates · pin = exact version · channel = receive beta builds</span></div>
        <form onSubmit={savePin} style={{ padding: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div className="form-row"><label>Tenant</label>
            <select value={pin.tenantId} onChange={(e) => setPin({ ...pin, tenantId: e.target.value })} required><option value="">Select…</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.slug}</option>)}</select></div>
          <div className="form-row"><label>Component</label>
            <select value={pin.component} onChange={(e) => setPin({ ...pin, component: e.target.value })}><option>agent</option><option>agent-server</option><option>dbscan</option></select></div>
          <div className="form-row"><label>Mode</label>
            <select value={pin.mode} onChange={(e) => setPin({ ...pin, mode: e.target.value })}><option>hold</option><option>pin</option><option>channel</option></select></div>
          {pin.mode === "pin" && <div className="form-row"><label>Version</label><input value={pin.version} onChange={(e) => setPin({ ...pin, version: e.target.value })} placeholder="0.2.0" required /></div>}
          {pin.mode === "channel" && <div className="form-row"><label>Channel</label><select value={pin.channel} onChange={(e) => setPin({ ...pin, channel: e.target.value })}><option>beta</option><option>stable</option></select></div>}
          <button className="btn primary">Save override</button>
        </form>
        <table>
          <thead><tr><th>Tenant</th><th>Component</th><th>Mode</th><th>Detail</th><th /></tr></thead>
          <tbody>
            {pins.map((p) => <tr key={p.id}><td>{tname(p.tenant_id)}</td><td>{p.component}</td><td>{p.mode}</td><td>{p.version || p.channel || "—"}</td><td><button className="btn sm" onClick={() => removePin(p)}>Remove</button></td></tr>)}
            {!pins.length && <tr><td colSpan={5} className="sub" style={{ padding: 16 }}>No overrides: every tenant follows the rollout %</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
